import { useQuery } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';

import { supabase } from '../lib/supabase';

export interface TeamMemberRow {
  id: string;
  organization_id: string;
  role: string;
  status: string;
}

/**
 * Prefix key — `invalidateQueries({ queryKey: teamMemberQueryKey })` clears the
 * membership of EVERY user in the cache. Kept as the invalidation entry point.
 *
 * The per-user key below is what the query itself registers under. Sharing one
 * key across users meant a signed-out user's result (including a stale failure)
 * was served to the next user who signed in on the same tab.
 */
export const teamMemberQueryKey = ['team-member'] as const;

export function teamMemberQueryKeyFor(userId: string) {
  return ['team-member', userId] as const;
}

async function fetchSelfTeamMember(userId: string): Promise<TeamMemberRow | null> {
  const { data, error } = await supabase
    .from('team_members')
    .select('id, organization_id, role, status')
    .eq('user_id', userId)
    .is('deleted_at', null)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export function useTeamMember(session: Session | null) {
  const userId = session?.user.id ?? null;
  return useQuery({
    // Scoped per user. Never collapse this back to a constant key.
    queryKey: userId ? teamMemberQueryKeyFor(userId) : teamMemberQueryKey,
    queryFn: () => {
      if (!userId) return Promise.resolve(null);
      return fetchSelfTeamMember(userId);
    },
    enabled: Boolean(userId),
    // Was Infinity, which made a transient failure permanent for the tab: the
    // gate in routes.tsx reads a missing result as "no organization" and sends
    // the user to /welcome, so one cached failure pinned them in that loop
    // until a full page reload.
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
}
